import React, { useEffect, useMemo, useState } from 'react';
import { Alert,  Chip, Snackbar, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import Header from '../../../organisms/header/header';
import apiClient from '../../../../utils/apiClient';
import decodeToken from '../../../../utils/decode-token';
import getTranslationApiErrorMessage from '../../../../utils/translation-api-error';
import getTranslationLanguageLabel from '../../../../utils/translation-language-label';
import {
  createTranslationUser,
  fetchTranslationUserById,
  updateTranslationUser,
} from '../../../../api/translationUsersApi';
import LanguageFilter from '../../../organisms/common/language-filter/language-filter';
import { isTranslationsFeatureEnvironment } from '../../../../utils/role-utils';
import '../new-admin/new-admin.scss';
import './new-translation-user.scss';

const SCOPED_ADMIN_ROLES = ['admin', 'countryAdmin', 'marketAdmin'];
const CREATOR_ROLES = [...SCOPED_ADMIN_ROLES, 'superAdmin'];
const EXCLUDED_ASSIGNMENT_LANGUAGES = (lang) => lang.startsWith('en');

const getAssignmentLabel = (languageName, countryName, showCountry) => {
  if (!showCountry) return languageName;

  const countrySuffix = `(${countryName})`;
  return languageName.endsWith(countrySuffix)
    ? languageName
    : `${languageName} ${countrySuffix}`;
};

const getBackendErrorMessage = (error) => {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }

  return responseData?.errorMessage || responseData?.message || responseData?.error || '';
};

const NewTranslationUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { translationUserRole, translationUserId } = useParams();
  const isEdit = Boolean(translationUserId);
  const token = sessionStorage.getItem('token');
  const currentRole = token ? decodeToken(token)?.role : null;
  const loggedUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('loggedUser') || '{}');
    } catch {
      return {};
    }
  }, []);
  const allowed = Boolean(
    token && CREATOR_ROLES.includes(currentRole) && isTranslationsFeatureEnvironment()
  );
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      role: 'translator',
      area_code: '',
      preferred_language: '',
      languages: [],
      phone: '',
      prefix_phone: '',
    },
  });
  const [countries, setCountries] = useState([]);
  const [editingAreaCode, setEditingAreaCode] = useState('');
  const [editPreferredLanguage, setEditPreferredLanguage] = useState('');
  const [phone, setPhone] = useState('');
  const [feedback, setFeedback] = useState({ open: false, severity: 'success', message: '' });
  const areaCode = watch('area_code');
  const selectedAssignments = watch('languages');
  useEffect(() => {
    if (!allowed || !isEdit) return;

    fetchTranslationUserById(translationUserRole, translationUserId)
      .then(({ data }) => {
        const loadedAreaCode = String(data.area_code || '').trim().toUpperCase();
        reset({
          role: data.role || translationUserRole || 'translator',
          name: data.name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          area_code: loadedAreaCode,
          preferred_language: data.preferred_language || '',
          languages: (data.languages || []).map((assignment) => (
            typeof assignment === 'string'
              ? assignment
              : `${assignment.country || assignment.area_code}:${assignment.language}`
          )),
        });
        setEditingAreaCode(loadedAreaCode);
        setEditPreferredLanguage(data.preferred_language || '');
        const loadedPrefix = data.prefix_phone || data.prefix || '';
        const loadedPhone = loadedPrefix + (data.phone || '');
        setPhone(loadedPhone);
        setValue('prefix_phone', loadedPrefix, { shouldDirty: false, shouldValidate: false });
        setValue('phone', loadedPhone, { shouldDirty: false, shouldValidate: false });
      })
      .catch(() => setFeedback({
        open: true,
        severity: 'error',
        message: t('common-internal-server-error'),
      }));
  }, [allowed, isEdit, reset, setValue, t, translationUserId, translationUserRole]);

  useEffect(() => {
    if (!allowed) return;
    apiClient.get('/hac/countries')
      .then(({ data }) => {
        const managedAreaCodes = new Set(loggedUser.managed_area_codes || []);
        let availableCountries = SCOPED_ADMIN_ROLES.includes(currentRole)
          ? data.filter((country) => managedAreaCodes.has(country.area_code))
          : data;

        if (isEdit && editingAreaCode && !availableCountries.some((country) => country.area_code === editingAreaCode)) {
          const editedUserCountry = data.find((country) => country.area_code === editingAreaCode);
          if (editedUserCountry) {
            availableCountries = [...availableCountries, editedUserCountry];
          }
        }

        setCountries(
          availableCountries
          .sort((first, second) => (
            t(`countries-${first.area_code}`).localeCompare(t(`countries-${second.area_code}`))
          )),
        );
      })
      .catch(() => setFeedback({
        open: true,
        severity: 'error',
        message: t('common-internal-server-error'),
      }));
  }, [allowed, currentRole, editingAreaCode, isEdit, loggedUser, t]);

  useEffect(() => {
    if (!isEdit || !editingAreaCode || countries.length === 0) return;

    const hasEditedAreaCode = countries.some((country) => country.area_code === editingAreaCode);
    if (!hasEditedAreaCode) return;

    setValue('area_code', editingAreaCode, { shouldDirty: false, shouldValidate: false });
    setValue('preferred_language', editPreferredLanguage, { shouldDirty: false, shouldValidate: false });
  }, [countries, editPreferredLanguage, editingAreaCode, isEdit, setValue]);

  useEffect(() => {
    register('languages', {
      validate: (value) => value.length > 0 || t('alert-language'),
    });
  }, [register, t]);

  const preferredLanguages = useMemo(
    () => countries.find((country) => country.area_code === areaCode)?.language || [],
    [areaCode, countries],
  );

  const languageAssignments = useMemo(() => countries.flatMap((country) => (
    (country.language || [])
      .filter((language) => !EXCLUDED_ASSIGNMENT_LANGUAGES(language))
      .map((language) => {
        const countriesForLanguage = countries.filter(({ language: countryLanguages = [] }) => (
          countryLanguages.includes(language)
        ));
        const showCountry = countriesForLanguage.length > 1;
        const countryName = t(`countries-${country.area_code}`);
        const languageName = getTranslationLanguageLabel(
          language,
          showCountry ? country.area_code : '',
          t,
        );

        return {
          country: country.area_code,
          countryName,
          language,
          languageName,
          assignmentName: getAssignmentLabel(languageName, countryName, showCountry && language !== 'en'),
          value: `${country.area_code}:${language}`,
        };
      })
  )), [countries, t]);

  const handleCountryChange = () => {
    setValue('preferred_language', '');
  };

  const selectedLanguageAssignments = useMemo(
    () => languageAssignments.filter(({ value }) => (selectedAssignments || []).includes(value)),
    [languageAssignments, selectedAssignments],
  );

  const validatePhone = (value) => {
    const phoneValue = value?.trim();

    if (!phoneValue) {
      return t('alert-phone');
    }

    const parsedPhone = parsePhoneNumberFromString(phoneValue);
    if (!parsedPhone?.isValid()) {
      return t('alert-phone-format');
    }

    return true;
  };

  const submit = async (data) => {
    const parsedPhone = parsePhoneNumberFromString(data.phone);
    if (!parsedPhone?.isValid()) {
      return;
    }

    const basePayload = {
      name: data.name,
      last_name: data.last_name,
      email: data.email,
      area_code: data.area_code,
      preferred_language: data.preferred_language,
      phone: parsedPhone.nationalNumber,
      languages: data.languages.map((assignment) => {
        const [country, language] = assignment.split(':');
        return { country, language };
      }),
    };

    const prefixValue = `+${parsedPhone.countryCallingCode}`;
    const payloadWithPrefixPhone = { ...basePayload, prefix_phone: prefixValue };
    const payloadWithPrefix = { ...basePayload, prefix: prefixValue };

    const saveUser = async (payload) => {
      if (isEdit) {
        // El rol no es editable: se usa siempre el del usuario que se está editando
        await updateTranslationUser(translationUserRole, translationUserId, payload);
        return;
      }

      await createTranslationUser(data.role, payload);
    };

    try {
      try {
        await saveUser(payloadWithPrefixPhone);
      } catch (error) {
        const backendErrorMessage = getBackendErrorMessage(error);
        const shouldRetryWithPrefix =
          error?.response?.status === 422 && backendErrorMessage.includes('"prefix_phone" is not allowed');

        if (!shouldRetryWithPrefix) {
          throw error;
        }

        await saveUser(payloadWithPrefix);
      }

      setFeedback({
        open: true,
        severity: 'success',
        message: isEdit
          ? t('alert-data-saved')
          : t('translation-user-create-success', {
            defaultValue: 'Translation user successfully created',
          }),
      });
      setTimeout(() => navigate('/admin/main/translator'), 2000);
    } catch (error) {
      setFeedback({
        open: true,
        severity: 'error',
        message: getTranslationApiErrorMessage(error, t, 'common-internal-server-error'),
      });
    }
  };

  if (!allowed) return <Navigate to={token ? '/admin/main' : '/login'} replace />;

  return (
    <div className="App">
      <Header />
      <main className="new-pma">
        <section className="contact-info">
          <div className="inner">
            <Link to="/admin/main/translator" className="back-button">
              <span className="icon-chevron-left"></span> {t('common-back')}
            </Link>
            <h2>
              {isEdit
                ? t('translation-user-details-title')
                : t('translation-user-create-title', { defaultValue: 'Create translation user' })}
            </h2>
            <form onSubmit={handleSubmit(submit)}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.role ? 'error' : ''}`}>
                    <label htmlFor="translation-role">
                      {t('translation-user-role-label', { defaultValue: 'Role' })}
                    </label>
                    <select
                      id="translation-role"
                      className="normal"
                      disabled={isEdit}
                      {...register('role', { required: !isEdit })}
                    >
                      <option value="translator">Translator</option>
                      <option value="reviewer">Reviewer</option>
                    </select>
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.email ? 'error' : ''}`}>
                    <label htmlFor="translation-email">{t('common-email')}</label>
                    <input
                      id="translation-email"
                      type="email"
                      {...register('email', { required: t('alert-email') })}
                    />
                    {errors.email && <span>{errors.email.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.name ? 'error' : ''}`}>
                    <label htmlFor="translation-name">{t('common-first-name')}</label>
                    <input
                      id="translation-name"
                      type="text"
                      {...register('name', { required: t('alert-name') })}
                    />
                    {errors.name && <span>{errors.name.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.last_name ? 'error' : ''}`}>
                    <label htmlFor="translation-last-name">{t('common-last-name')}</label>
                    <input
                      id="translation-last-name"
                      type="text"
                      {...register('last_name', { required: t('alert-last-name') })}
                    />
                    {errors.last_name && <span>{errors.last_name.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.phone ? 'error' : ''}`}>
                    <label>{t('common-phone')}</label>
                    <PhoneInput
                      key={areaCode}
                      defaultCountry={(areaCode || loggedUser.area_code || '').toLowerCase()}
                      value={phone}
                      onChange={(value) => {
                        setPhone(value);
                        setValue('phone', value, { shouldDirty: true, shouldValidate: false });

                        const parsedPhone = parsePhoneNumberFromString(value);
                        setValue(
                          'prefix_phone',
                          parsedPhone?.isValid() ? `+${parsedPhone.countryCallingCode}` : '',
                          { shouldDirty: true, shouldValidate: false },
                        );
                      }}
                    />
                    {errors.phone && <span>{errors.phone.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.area_code ? 'error' : ''}`}>
                    <label htmlFor="translation-country">{t('common-select-country')}</label>
                    <select
                      id="translation-country"
                      className="normal"
                      {...register('area_code', {
                        required: t('alert-country'),
                        onChange: handleCountryChange,
                      })}
                    >
                      <option value="">{t('common-select-country')}</option>
                      {countries.map((country) => (
                        <option key={country.area_code} value={country.area_code}>
                          {t(`countries-${country.area_code}`)}
                        </option>
                      ))}
                    </select>
                    {errors.area_code && <span>{errors.area_code.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.preferred_language ? 'error' : ''}`}>
                    <label htmlFor="translation-preferred-language">{t('common-select-language')}</label>
                    <select
                      id="translation-preferred-language"
                      className="normal"
                      {...register('preferred_language', { required: t('alert-language') })}
                    >
                      <option value="">{t('common-select-language')}</option>
                      {preferredLanguages.map((language) => (
                        <option key={language} value={language}>
                          {getTranslationLanguageLabel(language, areaCode, t)}
                        </option>
                      ))}
                    </select>
                    {errors.preferred_language && <span>{errors.preferred_language.message}</span>}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <div className={`form-control required ${errors.languages ? 'error' : ''}`}>
                    <label>{t('translation-user-languages-label', { defaultValue: 'Assigned languages' })}</label>
                    <LanguageFilter
                      options={languageAssignments}
                      value={selectedLanguageAssignments}
                      groupBy={(option) => option.countryName}
                      getOptionLabel={(option) => option.assignmentName}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_, selected) => {
                        setValue(
                          'languages',
                          selected.map(({ value }) => value),
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }}
                      renderTags={(selected, getTagProps) => selected.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.assignmentName}
                        />
                      ))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('translation-user-languages-placeholder', {
                            defaultValue: 'Search language',
                          })}
                          error={Boolean(errors.languages)}
                        />
                      )}
                    />
                    {errors.languages && <span>{errors.languages.message}</span>}
                  </div>
                </Grid>
                <input type="hidden" {...register('prefix_phone')} />
                <input
                  type="hidden"
                  {...register('phone', {
                    validate: validatePhone,
                  })}
                />
              </Grid>

              <div className="actions">
                <button className="btn" type="submit">
                  {isEdit
                    ? t('common-save')
                    : t('translation-user-create-action', { defaultValue: 'Create user' })}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback((current) => ({ ...current, open: false }))}
      >
        <Alert severity={feedback.severity} sx={{ width: '100%' }}>{feedback.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default NewTranslationUser;
